import { Button, ButtonGroup, Tooltip } from '@heroui/react'
import CopyButton from 'components/Hero/CopyButton'
import { formatDistanceToNow } from 'date-fns'
import { motion } from 'framer-motion'
import {
  Copy,
  Edit2,
  Folder,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import EditPathwayDetailsDialog from './EditPathwayDetailsDialog'

function PathwayCard({
  id,
  name,
  description,
  createdAt,
  setCurrId,
  setOpenDelete,
  setIsDuplicate,
  setOpen,
  folder,
  onMovePathway,
  setRefreshPathways,
}) {
  const navigate = useNavigate()
  const [editPathwayDetails, setEditPathwayDetails] = useState(false)

  const handleEditPathway = () => {
    navigate(`/dashboard/convo-pathways?id=${id}&view=splash`)
  }

  return (
    <div id="pathway-card-wrapper">
      <motion.div
        className="bg-white flex pathway-card justify-between flex-col relative cursor-pointer border h-full border-gray-300 rounded-sm overflow-hidden hover:shadow-md transition-all duration-300 ease-in-out"
        whileHover={{ y: -2 }}
        onClick={handleEditPathway}

      >
        <div
          className="flex flex-col p-3"
        >

          <div className="flex justify-between gap-2 items-start">
            <h3
              className="text-sm font-taurus font-medium text-gray-900 truncate"
              title={name}
            >
              {name}
            </h3>

            {folder && (
              <span className="flex shrink-0 items-center text-2xs text-gray-500 mt-0.5">
                <Folder className="w-2 h-2 mr-0.5" />
                <span title={folder.name}>{folder.name}</span>
              </span>
            )}
          </div>

          <p className="text-2xs text-gray-500">
            Created
            {' '}
            {' '}
            {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
          </p>

          <p
            className="text-xs text-gray-600 my-4 h-8 line-clamp-2"
          >
            {description}
          </p>

        </div>
        <ButtonGroup
          size="sm"
          color="default"
          variant="light"
          className="w-full p-1 bg-primary-50 border-t justify-end border-gray-300"
        >
          <CopyButton
            displayText={`${id?.substring(0, 8)}...`}
            value={id}
            tooltip="Copy pathway ID"
            color="default"
            className="mr-auto text-default-500"
          />
          <Tooltip content="Delete pathway">
            <Button
              color="danger"
              isIconOnly
              radius="none"
              onPress={() => {
                setCurrId(id)
                setOpenDelete(true)
              }}
              disableRipple
              className=""
            >
              <Trash2 size={14} />
            </Button>
          </Tooltip>
          <Tooltip content="Duplicate pathway">
            <Button
              isIconOnly
              onPress={() => {
                setCurrId(id)
                setIsDuplicate(true)
                setOpen(true)
              }}
              disableRipple
            >
              <Copy size={14} className="text-default-500" />
            </Button>
          </Tooltip>
          <Tooltip content="Move to folder">
            <Button
              onPress={() => onMovePathway(id)}
              isIconOnly
            >
              <Folder size={14} className="text-default-500" />
            </Button>
          </Tooltip>
          <Tooltip content="Edit details">
            <Button
              isIconOnly
              disableRipple
              onPress={() => setEditPathwayDetails(true)}
            >
              <Edit2 size={14} className="text-default-500" />
            </Button>
          </Tooltip>
        </ButtonGroup>

        <style jsx>
          {`#pathway-card-wrapper {
                position: relative;
              }
              
              #pathway-card-wrapper::before,
              #pathway-card-wrapper::after,
              #pathway-card-wrapper .before-elem,
              #pathway-card-wrapper .after-elem {
                content: '';
                position: absolute;
                inset: 0;
                transition: all 0.3s ease-out;
                opacity: 0;
              }
              
              #pathway-card-wrapper::before {
                background-color: #f59e0b; /* Yellow */
                z-index: 1;
              }
              
              #pathway-card-wrapper::after {
                background-color: #f97316; /* Orange */
                z-index: 2;
                transition-delay: 0.075s;
              }
              
              #pathway-card-wrapper .pathway-card {
                position: relative;
                z-index: 5;
              }
              
              #pathway-card-wrapper:hover::before {
                opacity: 1;
                transform: translate(-5px, 5px);
              }
              
              #pathway-card-wrapper:hover::after {
                opacity: 1;
                transform: translate(-5px, 5px);
              }
              
              #pathway-card-wrapper::before {
                content: '';
                position: absolute;
                inset: 0;
                background-color: #3b82f6; /* Blue */
                z-index: 3;
                transition-delay: 0.15s;
                opacity: 0;
              }
              
              #pathway-card-wrapper:hover::before {
                opacity: 1;
                transform: translate(-3px, 3px);
              }
            `}
        </style>

        {editPathwayDetails && (
          <EditPathwayDetailsDialog
            open={editPathwayDetails}
            onClose={() => setEditPathwayDetails(false)}
            pathwayId={id}
            pathwayName={name}
            pathwayDescription={description}
            setRefreshPathways={setRefreshPathways}
          />
        )}
      </motion.div>
    </div>
  )
}

export default PathwayCard
